import { motion } from "framer-motion";
import { FaShieldAlt, FaUserSecret, FaGlobe, FaBalanceScale } from "react-icons/fa";
import "../styles/about.css";

// Moroccan geometric corner pattern SVG
const MoroccanCorner = ({ position = "top-left" }) => (
  <svg
    className={`moroccan-corner ${position}`}
    viewBox="0 0 200 200"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.6" />
        <stop offset="100%" stopColor="#B8860B" stopOpacity="0.3" />
      </linearGradient>
    </defs>
    <g fill="none" stroke="url(#goldGradient)" strokeWidth="1.5">
      <polygon points="100,10 190,100 100,190 10,100" />
      <polygon points="100,30 170,100 100,170 30,100" />
      <polygon points="100,50 150,100 100,150 50,100" />
      <circle cx="100" cy="100" r="20" />
      <line x1="100" y1="10" x2="100" y2="80" />
      <line x1="100" y1="120" x2="100" y2="190" />
      <line x1="10" y1="100" x2="80" y2="100" />
      <line x1="120" y1="100" x2="190" y2="100" />
    </g>
  </svg>
);

// Decorative star/flower icon
const GoldStar = () => (
  <svg className="gold-star" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
    <g fill="#D4AF37">
      <polygon points="20,2 23,15 36,15 25,23 29,36 20,28 11,36 15,23 4,15 17,15" />
    </g>
  </svg>
);

function Privacy() {
  return (
    <div className="about-page">
      {/* Moroccan corner decorations */}
      <MoroccanCorner position="top-left" />
      <MoroccanCorner position="top-right" />

      {/* Hero Section */}
      <section className="hero-section">
        <h1 className="hero-title">Politique de Confidentialité</h1>
        <p className="hero-subtitle">
          Usage Académique et Recherche
        </p>
        <GoldStar />
        <p style={{ marginTop: "1rem", fontSize: "0.9rem", color: "var(--muted-foreground)" }}>
          Dernière mise à jour : Février 2026
        </p>
      </section>

      {/* Privacy Principles Section */}
      <section className="values-section">
        <h2 className="section-title">Nos Principes de Confidentialité</h2>
        <div className="values-grid">
          <motion.div
            className="value-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <div className="value-icon">
              <FaShieldAlt size={32} color="#D4AF37" />
            </div>
            <h3>Privacy-by-Design</h3>
            <p style={{ fontSize: "0.9rem", color: "var(--muted-foreground)", marginTop: "0.5rem" }}>
              Architecture Zero-PII Cloud
            </p>
          </motion.div>

          <motion.div
            className="value-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <div className="value-icon">
              <FaUserSecret size={32} color="#D4AF37" />
            </div>
            <h3>Pseudonymisation</h3>
            <p style={{ fontSize: "0.9rem", color: "var(--muted-foreground)", marginTop: "0.5rem" }}>
              Identifiants uniques seulement
            </p>
          </motion.div>

          <motion.div
            className="value-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <div className="value-icon">
              <FaGlobe size={32} color="#D4AF37" />
            </div>
            <h3>Services Tiers</h3>
            <p style={{ fontSize: "0.9rem", color: "var(--muted-foreground)", marginTop: "0.5rem" }}>
              Infrastructure cloud avancée
            </p>
          </motion.div>

          <motion.div
            className="value-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
          >
            <div className="value-icon">
              <FaBalanceScale size={32} color="#D4AF37" />
            </div>
            <h3>Vos Droits</h3>
            <p style={{ fontSize: "0.9rem", color: "var(--muted-foreground)", marginTop: "0.5rem" }}>
              Conformité Loi 09-08
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section 1: Data Minimization */}
      <section className="mission-section">
        <div className="mission-card">
          <div className="mission-text">
            <h2>1. Minimisation des Données et "Privacy-by-Design"</h2>
            <p>
              Notre plateforme utilise une architecture <strong>« Zero-PII Cloud »</strong> (Zéro Information Personnelle Identifiable sur le Cloud). 
              Nous ne stockons ni votre nom, ni votre adresse e-mail, ni votre photo de profil sur nos serveurs cloud.
            </p>
            <p>
              Toutes les données d'identification personnelles sont conservées strictement dans la mémoire temporaire de votre navigateur (Redux/RAM) 
              et sont effacées dès que vous vous déconnectez.
            </p>
          </div>
        </div>
      </section>

      {/* Section 2: Data Collection */}
      <section className="mission-section">
        <div className="mission-card">
          <div className="mission-text">
            <h2>2. Données Collectées (Pseudonymisation)</h2>
            <p>
              Afin de gérer l'accès aux fichiers et les quotas de téléchargement, nous stockons un identifiant alphanumérique unique (UID) généré par Google.
            </p>
            <ul style={{ marginLeft: "1.5rem", marginTop: "1rem", lineHeight: "1.8" }}>
              <li><strong>Sur Firebase :</strong> Nous stockons uniquement votre UID et votre compteur de téléchargements.</li>
              <li><strong>Sur Cloudflare :</strong> Nous conservons les journaux (logs) des requêtes de fichiers associés à votre adresse IP pour des raisons de sécurité.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Section 3: Third-Party Services */}
      <section className="mission-section">
        <div className="mission-card">
          <div className="mission-text">
            <h2>3. Services Tiers et Souveraineté des Données</h2>
            <p>
              Nous utilisons <strong>Google Firebase</strong> (États-Unis) et <strong>Cloudflare</strong> (Global) pour assurer la distribution haute performance des fichiers BIM.
            </p>
            <p>
              En utilisant ce service, vous reconnaissez que des métadonnées techniques (UID) sont traitées sur des serveurs internationaux. 
              Ce choix est justifié par une nécessité de <strong>« Recherche Scientifique »</strong> afin de bénéficier d'une infrastructure cloud avancée non disponible localement.
            </p>
          </div>
        </div>
      </section>

      {/* Section 4: Your Rights */}
      <section className="mission-section">
        <div className="mission-card">
          <div className="mission-text">
            <h2>4. Vos Droits (Loi 09-08)</h2>
            <p>
              Conformément à la loi marocaine 09-08, vous disposez d'un droit d'accès et de suppression de vos données.
            </p>
            <p>
              Puisque nous ne stockons qu'un UID aléatoire, vous devrez fournir votre UID (disponible dans vos paramètres de profil) pour toute demande de suppression.
            </p>
            <p style={{ marginTop: "1rem" }}>
              <strong>Contact :</strong>{" "}
              <a 
                href="mailto:contact@moroccandesignbim.com" 
                style={{ color: "var(--accent)", textDecoration: "underline" }}
              >
                contact@moroccandesignbim.com
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Technical Details Section */}
      <section className="offer-section">
        <h2 className="section-title">Détails Techniques</h2>
        <div className="offer-grid">
          <div className="offer-card">
            <h3>Stockage Local</h3>
            <p>Vos informations personnelles (nom, email, photo) sont stockées uniquement dans votre navigateur</p>
            <div className="offer-icon">
              <svg viewBox="0 0 30 30" fill="none" stroke="#D4AF37" strokeWidth="1.5">
                <rect x="5" y="8" width="20" height="14" rx="2" />
                <line x1="5" y1="13" x2="25" y2="13" />
                <circle cx="9" cy="10.5" r="1" />
                <circle cx="12" cy="10.5" r="1" />
                <circle cx="15" cy="10.5" r="1" />
              </svg>
            </div>
          </div>

          <div className="offer-card">
            <h3>Cloud Storage</h3>
            <p>Seul votre UID anonyme et vos compteurs de téléchargement sont stockés sur nos serveurs</p>
            <div className="offer-icon">
              <svg viewBox="0 0 30 30" fill="none" stroke="#D4AF37" strokeWidth="1.5">
                <path d="M8,18 Q8,12 13,12 Q13,8 18,8 Q23,8 23,13 Q28,13 28,18 Q28,23 23,23 L8,23 Q3,23 3,18 Q3,13 8,13" />
              </svg>
            </div>
          </div>

          <div className="offer-card">
            <h3>Déconnexion</h3>
            <p>Toutes vos données personnelles sont automatiquement effacées lors de la déconnexion</p>
            <div className="offer-icon">
              <svg viewBox="0 0 30 30" fill="none" stroke="#D4AF37" strokeWidth="1.5">
                <rect x="5" y="5" width="15" height="20" rx="2" />
                <path d="M13,10 L20,10 M20,10 L17,7 M20,10 L17,13" />
                <path d="M13,20 L20,20 M20,20 L17,17 M20,20 L17,23" />
              </svg>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Privacy;
